<template>
  <transition name="modal-fade">
    <div
      v-if="item"
      class="ticker-modal-overlay"
      @click.self="close"
    >
      <div
        class="ticker-modal glass-container steel-accent"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
      >
        <!-- Corner accents (matches the cookie banner / site aesthetic) -->
        <div class="corner-accent top-left"></div>
        <div class="corner-accent top-right"></div>
        <div class="corner-accent bottom-left"></div>
        <div class="corner-accent bottom-right"></div>

        <button
          class="modal-close"
          type="button"
          aria-label="Close"
          @click="close"
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <div class="modal-eyebrow">
          <span class="modal-dot" aria-hidden="true"></span>
          Available Stock
        </div>

        <div class="modal-body">
          <!-- Media: hero image + thumbnail slider -->
          <div class="modal-media">
            <div class="hero">
              <img
                v-if="hasImages && !heroError"
                :src="currentImage"
                :alt="title"
                class="hero-img"
                @error="heroError = true"
              />
              <div v-else class="hero-empty">
                <span class="hero-empty-icon" aria-hidden="true">🚛</span>
                <span>No pictures available</span>
              </div>
            </div>

            <div
              v-if="hasImages"
              class="thumbs"
              role="listbox"
              aria-label="Pictures"
            >
              <button
                v-for="(img, i) in item.images"
                :key="i"
                type="button"
                class="thumb"
                :class="{ active: i === activeIndex }"
                :aria-selected="i === activeIndex"
                @click="select(i)"
              >
                <img :src="img" :alt="`Thumbnail ${i + 1}`" />
              </button>
            </div>
          </div>

          <!-- Detail info -->
          <div class="modal-detail">
            <h2 v-if="detail.title" class="modal-title">{{ detail.title }}</h2>
            <p v-if="detail.heading" class="detail-heading">{{ detail.heading }}</p>
            <ul v-if="detail.specs && detail.specs.length" class="detail-specs">
              <li v-for="(spec, i) in detail.specs" :key="i">{{ spec }}</li>
            </ul>
            <p v-if="detail.footer" class="detail-footer">{{ detail.footer }}</p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'TickerDetailModal',
  props: {
    // The active ticker item, or null when the modal is closed.
    item: {
      type: Object,
      default: null,
    },
  },
  emits: ['close'],
  data() {
    return {
      activeIndex: 0,
      heroError: false,
    }
  },
  computed: {
    detail() {
      return (this.item && this.item.detail) || {}
    },
    hasImages() {
      return !!(this.item && this.item.images && this.item.images.length)
    },
    currentImage() {
      return this.hasImages ? this.item.images[this.activeIndex] : null
    },
    title() {
      return (this.detail && this.detail.title) || 'Available Stock'
    },
  },
  watch: {
    item(newItem) {
      // Reset the gallery whenever a (new) item opens.
      this.activeIndex = 0
      this.heroError = false
      if (newItem) {
        document.addEventListener('keydown', this.onKeydown)
        document.body.style.overflow = 'hidden'
      } else {
        this.cleanup()
      }
    },
  },
  beforeUnmount() {
    this.cleanup()
  },
  methods: {
    select(i) {
      this.activeIndex = i
      this.heroError = false
    },
    close() {
      this.$emit('close')
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close()
      }
    },
    cleanup() {
      document.removeEventListener('keydown', this.onKeydown)
      document.body.style.overflow = ''
    },
  },
}
</script>

<style scoped>
.ticker-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000; /* above the ticker (9000) and cookie banner (9999) */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(10, 10, 14, 0.72);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.ticker-modal {
  position: relative;
  width: min(880px, 94vw);
  max-height: 88vh;
  overflow-y: auto;
  padding: 1.75rem;
  background: rgba(29, 30, 38, 0.96);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.55),
    0 0 80px rgba(143, 0, 0, 0.08);
}

/* Corner accent decorations */
.corner-accent {
  position: absolute;
  width: 16px;
  height: 16px;
  pointer-events: none;
}
.corner-accent::before,
.corner-accent::after {
  content: '';
  position: absolute;
  background: linear-gradient(90deg, rgba(143, 0, 0, 0.8), rgba(183, 185, 186, 0.4));
}
.corner-accent::before { width: 16px; height: 2px; }
.corner-accent::after { width: 2px; height: 16px; }
.top-left { top: 0; left: 0; }
.top-left::before, .top-left::after { top: 0; left: 0; }
.top-right { top: 0; right: 0; }
.top-right::before, .top-right::after { top: 0; right: 0; }
.bottom-left { bottom: 0; left: 0; }
.bottom-left::before, .bottom-left::after { bottom: 0; left: 0; }
.bottom-right { bottom: 0; right: 0; }
.bottom-right::before, .bottom-right::after { bottom: 0; right: 0; }

.modal-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  font-size: 1.5rem;
  line-height: 1;
  color: rgba(183, 185, 186, 0.75);
  background: transparent;
  border: 1px solid rgba(183, 185, 186, 0.2);
  border-radius: 0.35rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.25, 1, 0.5, 1);
}
.modal-close:hover {
  color: #f0f0f0;
  background: linear-gradient(135deg, #8f0000, #a31a1a);
  border-color: transparent;
}

.modal-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'PT Sans', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(183, 185, 186, 0.95);
  margin-bottom: 1.1rem;
}
.modal-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff3b3b;
  box-shadow: 0 0 8px rgba(255, 59, 59, 0.8);
}

.modal-body {
  display: flex;
  gap: 1.6rem;
  align-items: flex-start;
}

/* Media column */
.modal-media {
  flex: 1 1 54%;
  min-width: 0;
}
.hero {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 0.5rem;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.hero-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: 'PT Sans', sans-serif;
  font-size: 0.85rem;
  color: rgba(183, 185, 186, 0.6);
}
.hero-empty-icon {
  font-size: 2rem;
  opacity: 0.55;
}

.thumbs {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.6rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
}
.thumb {
  flex: 0 0 auto;
  width: 68px;
  height: 50px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 0.35rem;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.35);
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.thumb:hover {
  opacity: 0.9;
}
.thumb.active {
  opacity: 1;
  border-color: #8f0000;
  transform: translateY(-1px);
}

/* Detail column */
.modal-detail {
  flex: 1 1 46%;
  min-width: 0;
  text-align: left;
}
.modal-title {
  font-family: 'PT Sans', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.6rem 0;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}
.detail-heading {
  font-family: 'PT Sans', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
  color: #f0f0f0;
  margin: 0 0 0.9rem 0;
}
.detail-specs {
  list-style: none;
  margin: 0 0 1rem 0;
  padding: 0;
}
.detail-specs li {
  position: relative;
  font-family: 'PT Sans', sans-serif;
  font-size: 0.92rem;
  line-height: 1.45;
  color: rgba(232, 233, 234, 0.92);
  padding: 0.28rem 0 0.28rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.detail-specs li::before {
  content: '◆';
  position: absolute;
  left: 0;
  top: 0.32rem;
  font-size: 0.6rem;
  color: rgba(143, 0, 0, 0.95);
}
.detail-footer {
  font-family: 'PT Sans', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(183, 185, 186, 0.95); /* steel silver */
  margin: 0.7rem 0 0 0;
}

/* Stack on narrow screens */
@media (max-width: 720px) {
  .ticker-modal {
    padding: 1.4rem;
  }
  .modal-body {
    flex-direction: column;
    gap: 1.1rem;
  }
  .modal-media,
  .modal-detail {
    flex: 1 1 auto;
    width: 100%;
  }
}

/* Fade + lift transition */
.modal-fade-enter-active {
  transition: opacity 0.3s ease;
}
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-active .ticker-modal {
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-from .ticker-modal {
  transform: translateY(16px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .modal-fade-enter-active,
  .modal-fade-leave-active,
  .modal-fade-enter-active .ticker-modal {
    transition: none;
  }
  .modal-fade-enter-from .ticker-modal {
    transform: none;
  }
}
</style>
